import type { Customer } from '../../customers';
import type { Item } from '../../items';
import type { Location } from '../../locations';
import type { BufferTimeInfo, ItemQuantityWithKit, ItemThumbnailSummary } from '../../types';

// Enums and types
export enum CheckoutStatus {
	OPEN = 'open',
	CREATING = 'creating',
	CLOSED = 'closed',
}

export type CheckoutStatusType = `${CheckoutStatus}`;

export interface Checkout {
	_id: string;
	fields?: Record<string, string>;
	name?: string | null;
	itemSummary: string;
	number: string | null;
	status: CheckoutStatus;
	customer: Customer | null;
	location: Location | null;
	created: string | null;
	modified: string | null;
	started: string | null;
	due: string | null;
	finished: string | null;
	archived: string | null;
	label: string | null;
	/**
	 * @deprecated use itemQuantitiesWithKits instead
	 * @see itemQuantitiesWithKits
	 */
	items?: Item[];
	/**
	 * @deprecated use itemQuantitiesWithKits instead
	 * @see itemQuantitiesWithKits
	 */
	itemQuantities?: Record<string, number>;
	itemQuantitiesWithKits?: ItemQuantityWithKit[];
	reservation?: string;
	itemThumbnails?: ItemThumbnailSummary;
	bufferTime?: BufferTimeInfo | null;
	overdue?: string | null;
}

// Variables
export interface CheckoutIdVariables {
	checkoutId: string;
}

export interface CheckoutIdsVariables {
	checkoutIds: string[];
}

// Constants

export const CHECKOUT_SYSTEM_FIELDS = [
	{
		id: 'from',
		name: 'From',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'to',
		name: 'To',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'contact',
		name: 'User',
		kind: 'customer',
		unit: null,
		excludeFromFilters: true, // covered by the users filter
	},
	{
		id: 'name',
		name: 'Name',
		kind: 'string',
		editor: 'text',
		unit: null,
	},
	{
		id: 'items',
		name: 'Items',
		kind: 'string',
		excludeFromFilters: true,
	},
	{
		id: 'location',
		name: 'Location',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'duration',
		name: 'Duration',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'number',
		name: 'Number',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'label',
		name: 'Label',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'created',
		name: 'Created',
		kind: 'datetime',
		editor: 'date',
		unit: null,
	},
];

export const USER_SYSTEM_FIELDS = [
	{
		id: 'customer.email',
		name: 'User email',
		kind: 'string',
		editor: 'email',
		unit: null,
	},
];

export const fieldIdsByName = {
	from: 'started' as const,
	to: 'due' as const,
	contact: 'customer' as const,
};

export const CHECKOUT_FIELDS = [
	'_id',
	'name',
	'itemSummary',
	'number',
	'status',
	'customer._id',
	'customer.status',
	'customer.name',
	'customer.kind',
	'location._id',
	'location.name',
	'location.status',
	'started',
	'due',
	'finished',
	'archived',
	'label',
	'reservation',
	'itemQuantitiesWithKits',
] as const;

export type CheckoutCustomFieldKey = `fields.${string}` | `customer.fields.${string}` | `customer.${string}`;

export type CheckoutSystemField =
	| 'from'
	| 'to'
	| 'contact'
	| 'name'
	| 'items'
	| 'location'
	| 'duration'
	| 'number'
	| 'label'
	| 'created';

export type CheckoutField = CheckoutSystemField | CheckoutCustomFieldKey;
